import { serviceErrorMessage } from '../utils/serviceErrors';

export interface ChainStep {
  readonly label: string;
  readonly mutateAsync: () => Promise<unknown>;
}

export interface ChainResult {
  readonly completed: number;
  readonly stopped: boolean;
  readonly error?: string;
}

/**
 * D9-02 — the sequential chained-CTA runner behind a "quick path" button that
 * fires more than one verb (e.g. confirm-then-check-in). It awaits each step
 * in order and stops at the first rejection; it does NOT add retry, rollback
 * or compensation logic, because there are no compound endpoints (D7-01
 * rejected them) and there is nothing to roll back.
 *
 * "Invalidates after each step, not once at the end" is a property of the
 * COMPOSED system, not of this runner in isolation: every `mutateAsync`
 * passed in a step is expected to be one of this module's own verb mutation
 * hooks (e.g. `useConfirmWorkOrder().mutateAsync`), and each of those already
 * calls `useInvalidateWorkOrder()` in its own `onSuccess`. Awaiting one step
 * fully before starting the next means that step's invalidation always lands
 * before the next request fires — so a mid-chain failure leaves the query
 * cache, and the screen it feeds, showing the state actually reached. The
 * advisor sees that reached state's own next legal primary action next; the
 * failure is surfaced through `serviceErrorMessage`, never hidden and never
 * silently retried.
 */
export function useVerbChain() {
  async function run(steps: readonly ChainStep[]): Promise<ChainResult> {
    let completed = 0;

    for (const step of steps) {
      try {
        await step.mutateAsync();
        completed += 1;
      } catch (error) {
        return {
          completed,
          stopped: true,
          error: serviceErrorMessage(error, step.label).message,
        };
      }
    }

    return { completed, stopped: false };
  }

  return { run };
}
